/**
 * WHAT "UNREAD" MEANS, ONCE, FOR MESSAGES AND FOR EVERY NUMBER THAT COUNTS IT.
 *
 * Bobbie, 2026-10-04 (DECISIONS 100), asked what the home "Waiting on you"
 * count should hold: "Every unread message". It matches the unread count in
 * Messages. Bug B17: it did not. Kate saw a badge with nothing inside, and
 * the count and the Messages list had been built two different ways since
 * 2026-09-09:
 *
 *   - The family count read the raw bell cards. Messages folds a card into
 *     the row that already shows its source: a conversation's message card
 *     lives inside the conversation row, a class post's card inside the post
 *     row. So a card left unread under a conversation she had already read
 *     counted 1 on the home screen while Messages showed nothing unread, and
 *     three cards in one conversation counted 3 over one row.
 *   - The staff count added must-reads and mentions one MESSAGE at a time,
 *     while Messages draws one row per conversation, and a conversation with
 *     an unanswered must-read was not marked unread in Messages at all once
 *     she had read it, answered it or tapped Done with this.
 *   - A studio announcement or class post she had reacted to counted on the
 *     family home and not in Messages, which takes her own reaction as the
 *     answer (Bobbie's rule, DECISIONS 2026-08-18).
 *   - The staff count left out every card Messages shows as unread (comp,
 *     Jr Staff and parent board posts and replies, class replies, award
 *     notes), new replies on a staff announcement, and owed studio
 *     announcements and class posts.
 *
 * Everything here is the rule both sides run: Messages decides each row's
 * unread mark with it, and the home row, the bell, the phone badge, the
 * Messages tile and the menu count its rows with it. The number counts ROWS,
 * the things Messages draws, so a conversation is one however many messages
 * in it are waiting.
 *
 * TO-DOS AND SUB REQUESTS ARE NOT IN IT. Both are rows in Messages, and both
 * already have their own row on the home screen (Open to-dos for you, Sub
 * requests you could claim). Counting them here too would put the same thing
 * on the home screen twice. The cards behind them are left out of this count
 * for the same reason.
 *
 * Framework free, so it is tested without Firebase or React
 * (tests/messages-unread-core.test.mjs).
 */

import {
  notificationInboxSource,
  notificationMatchesDiscussion,
  notificationMatchesSubRequest,
  notificationMatchesTask,
  type CompletionNotificationRef,
} from './notificationCompletion.ts';
import { groupNotificationsForInbox, type GroupableNotification, type NotifInboxGroup } from './compBoardNotifGroups.ts';
import { notificationHandledAtSource, type HandledSources, type MergeableNotification } from './familyFeedMerge.ts';
import { classPostAckExempt } from './classPostAckCore.ts';
import { bulletinAckOwedBy, bulletinActedOnBy, type BulletinEpochFields } from './bulletinEpoch.ts';
import { authoredByMe, markedByAnyOfMine, myUids, type PerPersonViewer } from './jrStaffPerPersonState.ts';
import { groupClassPostsForInbox } from './messageInbox.ts';
import { tsToMillis } from './tonight.ts';
import { announcementHandledByViewer, announcementNeedsAck } from './messagesDoneFlow.ts';
import type { MessageInboxStateDoc } from './messageInboxStateCore.ts';
import type { ClassPostDoc } from './classPosts';

// ---------------------------------------------------------------------------
// Which row shows a bell card
// ---------------------------------------------------------------------------

/**
 * The rows a page draws, by the id of what each row is about. `'every'` means
 * the caller cannot list them and takes the row to be there, which is always
 * true for a card about something that was sent to her and still exists. A
 * home count that does not hold the class post list uses it, so a class post
 * card is never counted on its own beside the post row Messages folds it
 * into. `null` means the page has no rows of that kind at all for this viewer
 * (a family has no staff announcement or sub request rows), so the card is
 * drawn as its own row.
 */
export type OwnRowIds = ReadonlySet<string> | 'every';

export interface OwnRowIndex {
  threadIds: OwnRowIds;
  classPostIds: OwnRowIds;
  taskIds: OwnRowIds;
  discussionIds: OwnRowIds | null;
  subRequestIds: OwnRowIds | null;
}

/** The bell card fields read here: the narrowest of the shapes the three
 *  shared helpers take, so a NotificationDoc fits all of them. */
export interface UnreadNotificationLike {
  id: string;
  /** Whose card: the legacy award fold in notificationMatchesTask reads it (DECISIONS 170). */
  uid?: string;
  kind: string;
  read?: boolean;
  dismissed?: boolean;
  threadId?: string;
  communityThreadId?: string;
  taskId?: string;
  classPostId?: string;
  classReplyId?: string;
  discussionId?: string;
  compBoardPostId?: string;
  compBoardReplyId?: string;
  subRequestId?: string;
  bulletinId?: string;
  resendEpoch?: unknown;
  title?: unknown;
  link?: unknown;
  createdAt?: unknown;
  manuallyUnreadAt?: unknown;
}

// Compile-time proof the card shape fits every helper it is handed to.
type Fits<A, B> = A extends B ? true : never;
const unreadNotificationFits: [
  Fits<UnreadNotificationLike, CompletionNotificationRef>,
  Fits<UnreadNotificationLike, MergeableNotification>,
  Fits<UnreadNotificationLike, GroupableNotification>,
] = [true, true, true];
void unreadNotificationFits;

function hasRow(ids: OwnRowIds, id: string): boolean {
  return ids === 'every' ? true : ids.has(id);
}

function anyRowMatches(ids: OwnRowIds, matches: (id: string) => boolean): boolean {
  if (ids === 'every') return true;
  for (const id of ids) if (matches(id)) return true;
  return false;
}

/**
 * Whether a bell card is already shown by the row for its source, and so is
 * NOT a row of its own in Messages. Moved here from Messages.tsx
 * (notificationAlreadyRenderedElsewhere), unchanged in what it answers, so the
 * count asks the same question.
 *
 * The orphan guard stays: a card whose source is not on the page (a deleted
 * conversation, a class post this viewer's page does not draw) is its own
 * row, so it is never hidden with nowhere to find it.
 */
export function notificationShownByOwnRow(n: UnreadNotificationLike, rows: OwnRowIndex): boolean {
  const source = notificationInboxSource(n);
  if (source?.kind === 'thread') return hasRow(rows.threadIds, source.itemId);
  // A class REPLY card is its own row (Bobbie, 2026-09-28: "messages dont'
  // always show everything"): the post row opens with the replies closed.
  if (source?.kind === 'classPost' && n.classReplyId) return false;
  if (source?.kind === 'classPost') return hasRow(rows.classPostIds, source.itemId);
  if (n.kind === 'task') return anyRowMatches(rows.taskIds, (id) => notificationMatchesTask(n, id));
  if (n.kind === 'bulletin') return true; // the announcement row covers every announcement card
  if (n.discussionId) {
    return rows.discussionIds !== null
      && anyRowMatches(rows.discussionIds, (id) => notificationMatchesDiscussion(n, id));
  }
  if (n.subRequestId) {
    return rows.subRequestIds !== null
      && anyRowMatches(rows.subRequestIds, (id) => notificationMatchesSubRequest(n, id));
  }
  return false;
}

/**
 * A card row is done once its newest card is read or filed, or once she
 * already answered the class post or announcement every card in it is about.
 * Messages files a done row under Past; anything else is unread in Main.
 *
 * READ counts (2026-08-20, Bobbie: "I have read them all in the past and now
 * they're back again as unread"). The answered-at-source half is the family
 * home's rule from 2026-09-24 ("I've marked everything done. They keep coming
 * back up."), which Messages now runs too, so a card from before the clearing
 * fix files itself in both places at once.
 */
export function notificationRowDone<T extends UnreadNotificationLike>(
  group: Pick<NotifInboxGroup<T>, 'representative' | 'cards'>,
  handled: HandledSources = {}
): boolean {
  const newest = group.representative;
  if (newest.dismissed === true || newest.read === true) return true;
  return group.cards.length > 0 && group.cards.every((card) => notificationHandledAtSource(card, handled));
}

/** A to-do or sub request card. It has its own row on the home screen. */
function cardForHomeOwnRow(n: UnreadNotificationLike): boolean {
  return n.kind === 'task' || Boolean(n.taskId) || Boolean(n.subRequestId);
}

/**
 * The card rows Messages shows as unread, for a count. `cards` is the bell
 * list newest first, read cards included: a comp board post's cards make one
 * row, and the row is unread only while its NEWEST card is, exactly as
 * Messages decides it. `isSnoozed` is the row's Remind me later, read off its
 * newest card.
 */
export function unreadNotificationRows<T extends UnreadNotificationLike>(
  cards: readonly T[],
  rows: OwnRowIndex,
  options: { isSnoozed?: (cardId: string) => boolean; handled?: HandledSources } = {}
): NotifInboxGroup<T>[] {
  const own = cards.filter((card) => card.dismissed !== true
    && !cardForHomeOwnRow(card)
    && !notificationShownByOwnRow(card, rows));
  return groupNotificationsForInbox(own).filter((group) =>
    !options.isSnoozed?.(group.representative.id)
    && !notificationRowDone(group, options.handled));
}

// ---------------------------------------------------------------------------
// Rows that wait on an answer
// ---------------------------------------------------------------------------

/** Just the class post fields classPostOwesMyAck reads. */
export interface OwedClassPostFields {
  ackRequired?: boolean;
  authorUid: string;
  expectedRecipientUids?: string[];
  backfilledRecipientUids?: string[];
  acks?: Record<string, unknown>;
  dismissedBy?: Record<string, unknown>;
  reactions?: Record<string, unknown>;
}

/**
 * A class post still owes HER an answer. Moved from Messages.tsx
 * (classPostNeedsAck), unchanged: she is on its recipient list under one of
 * her sign-ins and was not added later, she did not write it, and none of
 * her sign-ins has answered it, dismissed it, or reacted to it. Her own
 * reaction is her answer (Bobbie's rule; a reaction from before the reacting
 * surfaces stamped the ack still counts). ONE PERSON, HOWEVER SHE SIGNS IN:
 * `viewer` is every uid a linked Jr Staff holds.
 */
export function classPostOwesMyAck(post: OwedClassPostFields, viewer: PerPersonViewer): boolean {
  const expectedUids = post.expectedRecipientUids ?? [];
  const mine = myUids(viewer);
  return (
    !!post.ackRequired
    && mine.some((uid) => expectedUids.includes(uid) && !classPostAckExempt(post, uid))
    && !authoredByMe(post.authorUid, mine)
    && !markedByAnyOfMine(post.acks, mine)
    && !markedByAnyOfMine(post.dismissedBy, mine)
    && !markedByAnyOfMine(post.reactions, mine)
  );
}

/**
 * The class post rows that still owe her an answer: one per broadcast group,
 * the same grouping Messages draws, with a row she snoozed on this device
 * left out. `isRowSnoozed` gets the row id; pass the same check Messages
 * hides its rows with (attentionSnooze.ts isNotifSnoozed). Returns the owed
 * posts' own ids.
 */
export function owedClassPostRows(
  posts: readonly ClassPostDoc[],
  viewer: PerPersonViewer,
  isRowSnoozed: (rowId: string) => boolean = () => false
): Set<string> {
  const ids = new Set<string>();
  if (myUids(viewer).length === 0) return ids;
  for (const group of groupClassPostsForInbox(posts)) {
    // Snoozed by the ROW id Messages draws and snoozes (group.id:
    // `classPost-<id>`, or `classPostBroadcast-<broadcastId>` for a one-tap
    // send), never the raw post id (PR #1452 review, B17).
    if (isRowSnoozed(group.id)) continue;
    const owed = group.posts.find((post) => classPostOwesMyAck(post, viewer));
    if (owed) ids.add(owed.id);
  }
  return ids;
}

/**
 * Which posts in her own class post window Messages draws at all, injected so
 * this file stays free of Firebase: classPosts.ts isClassPostRetracted, and
 * for a staff sign-in smokeFixture.ts isHiddenFixturePost, the two filters
 * useClassPostInbox runs on the list Messages reads.
 */
export interface AckWindowFilters<T> {
  isRetracted: (post: T) => boolean;
  isHidden?: (post: T) => boolean;
}

/**
 * The class posts in her own window (FAMILY_CLASS_POST_ACK_WINDOW, every post
 * sent to the uid signed in) that still owe HER an answer and that Messages
 * would draw. Messages folds these into its own list (withOwedClassPosts), so
 * a post she owes always has a row there, however busy the studio-wide list
 * a staff member's Messages reads. The count counts exactly these.
 */
export function owedClassPostsInAckWindow<T extends ClassPostDoc>(
  ackWindow: readonly T[],
  viewer: PerPersonViewer,
  filters: AckWindowFilters<T>
): T[] {
  return ackWindow.filter((post) => !filters.isRetracted(post)
    && !filters.isHidden?.(post)
    && classPostOwesMyAck(post, viewer));
}

/**
 * Messages' own class post list with the owed posts from her window folded
 * in: one copy of each post, newest first, the order groupClassPostsForInbox
 * expects. The list comes back unchanged when nothing is added.
 */
export function withOwedClassPosts<T extends ClassPostDoc>(
  windowPosts: readonly T[],
  owed: readonly T[]
): T[] {
  const ids = new Set(windowPosts.map((post) => post.id));
  const extra = owed.filter((post) => !ids.has(post.id));
  if (extra.length === 0) return windowPosts as T[];
  const ms = (post: T) => tsToMillis(post.createdAt) ?? 0;
  return [...windowPosts, ...extra].sort((a, b) => ms(b) - ms(a));
}

/** Just the announcement fields studioAnnouncementOwesMyAck reads. */
export interface OwedBulletinFields extends BulletinEpochFields {
  ackRequired?: boolean;
  expectedRecipientUids?: string[];
  createdBy?: string;
  reactions?: Record<string, unknown>;
}

/**
 * A studio announcement still owes this sign-in an answer at its current Show
 * again round. Moved from Messages.tsx (bulletinNeedsAck), unchanged in what
 * it answers: a reaction from before the current round does not count, one
 * from this round does, whether or not the reacting surface stamped the ack
 * slot (Bobbie, 2026-10-25, "Count as acknowledged"). The rule itself is
 * bulletinAckOwedBy (bulletinEpoch.ts), which the Announcements page asks too.
 */
export function studioAnnouncementOwesAck(bulletin: OwedBulletinFields, uid: string): boolean {
  return bulletinAckOwedBy(bulletin, uid);
}

/** The same question for HER, on every sign-in she holds. */
export function studioAnnouncementOwesMyAck(bulletin: OwedBulletinFields, viewer: PerPersonViewer): boolean {
  const mine = myUids(viewer);
  return mine.some((uid) => studioAnnouncementOwesAck(bulletin, uid))
    && !mine.some((uid) => bulletinActedOnBy(bulletin, uid));
}

// ---------------------------------------------------------------------------
// Staff announcements
// ---------------------------------------------------------------------------

/** Just the staff announcement fields read here. */
export interface StaffAnnouncementFields {
  authorUid: string;
  ackRequired?: boolean;
  acks?: Readonly<Record<string, unknown>>;
  dismissedBy?: Readonly<Record<string, unknown>>;
  createdAt?: unknown;
  lastReplyAt?: unknown;
  editedAt?: unknown;
  replyCount?: number;
  seenReplyCount?: Readonly<Record<string, number>>;
}

/**
 * The unread mark on a staff announcement row (StaffAnnouncementRow): it asks
 * her for an acknowledgement she has not given, or it has replies she has not
 * opened (seenReplyCount, board.ts). Only real signals, never a guess.
 */
export function staffAnnouncementUnreadMark(
  discussion: StaffAnnouncementFields,
  meUid: string,
  participatesInAudience: boolean
): boolean {
  if (announcementNeedsAck(discussion, meUid, participatesInAudience)) return true;
  if (!participatesInAudience || !meUid) return false;
  const seen = discussion.seenReplyCount?.[meUid] ?? 0;
  return Math.max(0, (discussion.replyCount ?? 0) - seen) > 0;
}

/**
 * A staff announcement row that counts: in Main (not acknowledged, dismissed
 * or filed by any of her sign-ins, the same test Messages files rows to Past
 * with) and carrying the unread mark above.
 */
export function staffAnnouncementRowUnread(
  discussion: StaffAnnouncementFields,
  viewer: PerPersonViewer,
  meUid: string,
  participatesInAudience: boolean,
  inboxState?: Pick<MessageInboxStateDoc, 'filedThroughMs'>
): boolean {
  const mine = myUids(viewer);
  if (mine.some((uid) => announcementHandledByViewer(discussion, uid, inboxState, participatesInAudience))) {
    return false;
  }
  return staffAnnouncementUnreadMark(discussion, meUid, participatesInAudience);
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/**
 * A conversation row is unread when something in it is newer than her read
 * cursor (threadUnreadSinceMs, the dot Messages always drew), OR when it holds
 * a must-read she has not answered. Nothing but an answer clears a must-read
 * (DECISIONS 2026-08-18), so reading the conversation, answering it or filing
 * it leaves the row unread and in Main with its Must read mark. It used to go
 * quiet in Messages while the home screen still counted it.
 */
export function conversationRowUnread(unreadByCursor: boolean, owesMustRead: boolean): boolean {
  return unreadByCursor || owesMustRead;
}

// ---------------------------------------------------------------------------
// The count
// ---------------------------------------------------------------------------

export interface WaitingRowsInput {
  /** The conversations on her list. A must-read or a mention in a
   *  conversation the list does not hold has no row to count. */
  threadIds: ReadonlySet<string>;
  /** Conversations unread by the read cursor (unreadThreadCursorsFrom). */
  unreadThreadIds: ReadonlySet<string>;
  /** Conversations holding a must-read she has not answered. */
  mustReadThreadIds: ReadonlySet<string>;
  /** Conversations with a recent mention of her still unread, for the
   *  breakdown only: a mention never adds a row of its own. */
  mentionThreadIds?: ReadonlySet<string>;
  /** unreadNotificationRows(...).length */
  updateRows?: number;
  /** owedClassPostRows(...).size */
  classPostRows?: number;
  /** Studio announcements still owed an answer. */
  announcementRows?: number;
  /** Staff announcements whose row is unread (staffAnnouncementRowUnread). */
  staffAnnouncementRows?: number;
}

export interface WaitingRows {
  total: number;
  mustReads: number;
  mentions: number;
  unread: number;
  updates: number;
  classPosts: number;
  announcements: number;
  staffAnnouncements: number;
}

/**
 * How many rows Messages shows as unread, and what they are. A conversation
 * is one row: a must-read if it holds one, otherwise a mention if it names
 * her, otherwise plain unread. Every input is a row count, so nothing is
 * counted twice.
 */
export function waitingOnYouRows(input: WaitingRowsInput): WaitingRows {
  let mustReads = 0;
  let mentions = 0;
  let unread = 0;
  const seen = new Set<string>();
  const consider = (threadId: string) => {
    if (seen.has(threadId) || !input.threadIds.has(threadId)) return;
    seen.add(threadId);
    if (input.mustReadThreadIds.has(threadId)) mustReads += 1;
    else if (input.mentionThreadIds?.has(threadId)) mentions += 1;
    else unread += 1;
  };
  for (const threadId of input.mustReadThreadIds) consider(threadId);
  for (const threadId of input.unreadThreadIds) consider(threadId);
  const clean = (n: number | undefined) => Math.max(0, Math.floor(Number.isFinite(n) ? (n as number) : 0));
  const updates = clean(input.updateRows);
  const classPosts = clean(input.classPostRows);
  const announcements = clean(input.announcementRows);
  const staffAnnouncements = clean(input.staffAnnouncementRows);
  return {
    total: mustReads + mentions + unread + updates + classPosts + announcements + staffAnnouncements,
    mustReads,
    mentions,
    unread,
    updates,
    classPosts,
    announcements,
    staffAnnouncements,
  };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The small line under the home row: what the number is made of. */
export function waitingRowsBreakdown(rows: WaitingRows): string {
  const parts: string[] = [];
  if (rows.mustReads) parts.push(plural(rows.mustReads, 'must-read', 'must-reads'));
  if (rows.mentions) parts.push(plural(rows.mentions, 'mention', 'mentions'));
  if (rows.unread) parts.push(`${rows.unread} unread`);
  if (rows.updates) parts.push(plural(rows.updates, 'update', 'updates'));
  if (rows.classPosts) parts.push(plural(rows.classPosts, 'class post', 'class posts'));
  if (rows.announcements) parts.push(plural(rows.announcements, 'studio announcement', 'studio announcements'));
  if (rows.staffAnnouncements) parts.push(plural(rows.staffAnnouncements, 'staff announcement', 'staff announcements'));
  return parts.join(' | ');
}

export interface MessageWaitingInput<T extends UnreadNotificationLike> {
  /** Her bell cards, newest first, read ones included (useMyNotifications). */
  notifications: readonly T[];
  /** The conversations on her list. */
  threadIds: ReadonlySet<string>;
  /** The other rows the page draws, for folding a card into its source. */
  rows: Omit<OwnRowIndex, 'threadIds'>;
  unreadThreadIds: ReadonlySet<string>;
  mustReadThreadIds?: ReadonlySet<string>;
  mentionThreadIds?: ReadonlySet<string>;
  /** owedClassPostRows(...) */
  owedClassPostIds?: ReadonlySet<string>;
  /** Studio announcements still owed an answer (studioAnnouncementOwesMyAck). */
  owedAnnouncements?: number;
  /** Staff announcement rows that count (staffAnnouncementRowUnread). */
  staffAnnouncementRows?: number;
  isSnoozed?: (cardId: string) => boolean;
  handled?: HandledSources;
}

/**
 * THE number: every row Messages shows as unread, from the data a counter
 * holds. The family home, the family badge, the staff home, the staff bell,
 * the staff badge, the Messages tile and the menu all call this.
 */
export function messageWaitingRows<T extends UnreadNotificationLike>(input: MessageWaitingInput<T>): WaitingRows {
  const updates = unreadNotificationRows(
    input.notifications,
    { ...input.rows, threadIds: input.threadIds },
    { isSnoozed: input.isSnoozed, handled: input.handled }
  );
  return waitingOnYouRows({
    threadIds: input.threadIds,
    unreadThreadIds: input.unreadThreadIds,
    mustReadThreadIds: input.mustReadThreadIds ?? new Set(),
    mentionThreadIds: input.mentionThreadIds,
    updateRows: updates.length,
    classPostRows: input.owedClassPostIds?.size ?? 0,
    announcementRows: input.owedAnnouncements ?? 0,
    staffAnnouncementRows: input.staffAnnouncementRows ?? 0,
  });
}
